import { ModuleType, PageType, SubPageType } from '../enums/page-type.enum';
import { LoggingService } from '../services/logging.service';

export interface PagePerformanceMetrics {
  networkTime?: number; // Time spent in network requests ~ total network processing time
  renderTime?: number; // Time spent rendering the page
  totalPageLoadTime?: number; // Total time spent loading the page || Time to First Contentful Paint (FCP)
  apiRequestCount?: number; // Number of API requests made
  apiRequestsTime?: number; // Total time spent on API requests
  requestDetails?: { // Details of individual requests
    url: string;
    duration: number;
  }[];
}

export interface PageViewProperties extends PagePerformanceMetrics {
  module: ModuleType;
  page: PageType;
  subPage?: SubPageType;
  [key: string]: any;
}

/**
 * Tracks page navigation and loading time for analytics
 *
 * This class manages:
 * - The current page context (module/page/subPage)
 * - Request counting for concurrent API calls
 * - Timing information for page loading
 * - Analytics logging for page views
 */
export class PageLoadingContext {
  private module: ModuleType;
  private page: PageType;
  private subPage: SubPageType;

  private started: Date;
  private ended: Date;
  private requestCount = 0;

  // Enhanced tracking for page loading
  private firstRequestTime: number = null;
  private lastRequestTime: number = null;
  private requestDetails = new Map<string, { duration: number }>();
  private renderStartTime: number = null;

  private timerId: number;
  private lastLoggedPageName: string;
  private performanceObserver: PerformanceObserver;

  constructor(private loggingService: LoggingService) {
    // Set up performance observer to track page render time if supported
    if (typeof PerformanceObserver !== 'undefined') {
      try {
        this.performanceObserver = new PerformanceObserver(entryList => {
          for (const entry of entryList.getEntries()) {
            if (entry.entryType === 'paint' && entry.name === 'first-contentful-paint') {
              this.renderStartTime = entry.startTime;
            }
          }
        });
        this.performanceObserver.observe({ entryTypes: ['paint'] });
      } catch (e) {
        console.warn('Performance observer not supported', e);
      }
    }
  }

  /**
   * Start tracking a page view
   * Increments request count if the same page is already being tracked
   */
  private start(module: ModuleType, page: PageType, subPage: SubPageType): void {
    if (this.lastLoggedPageName === this.getPageName(module, page, subPage)) {
      return;
    }

    this.clearTimer();

    // Check if this is the same page as already being tracked
    if (this.module === module && this.page === page && this.subPage === subPage) {
      this.requestCount++;
      return;
    }

    this.module = module;
    this.page = page;
    this.subPage = subPage;
    this.started = new Date();
    this.ended = null;
    this.requestCount = 1;

    // Reset request tracking
    this.firstRequestTime = null;
    this.lastRequestTime = null;
    this.requestDetails = new Map();
    this.renderStartTime = null;
  }

  /**
   * End tracking for the current request
   * Logs the page view when all requests are complete
   */
  end(): void {
    this.ended = new Date();
    this.requestCount = Math.max(0, this.requestCount - 1);

    if (this.requestCount === 0) {
      this.clearTimer();
      this.timerId = setTimeout(() => this.logPageView(), 500) as any as number;
    }
  }

  /**
   * Track the start of an API request for the current page
   * @param url The URL being requested
   * @param moduleType Optional module type - if provided, will ensure page context is started
   * @param pageType Optional page type - if provided, will ensure page context is started
   * @param subPageType Optional sub page type - if provided, will ensure page context is started
   */
  trackRequestStart(url: string, moduleType?: ModuleType, pageType?: PageType, subPageType?: SubPageType): void {
    // Initialize page context if module and page are provided
    if (moduleType !== undefined && pageType !== undefined) {
      this.start(moduleType, pageType, subPageType);
    }

    const now = performance.now();

    if (this.firstRequestTime === null) {
      this.firstRequestTime = now;
    }

    // Store request start information
    this.requestDetails.set(url, {
      duration: 0,
    });
  }

  /**
 * Track the completion of an API request and potentially end page tracking
 * @param url The URL that was requested
 * @param endPageTracking Whether to decrement the request count and potentially log the page view
 */
  trackRequestEnd(url: string): void {
    const now = performance.now();
    this.lastRequestTime = now;

    // Find and update the matching request
    const request = this.requestDetails.get(url);
    if (request) {
      request.duration = now - this.firstRequestTime;
    }
  }

  /**
   * Clear all tracking data
   */
  clear(): void {
    this.clearTimer();
    this.module = null;
    this.page = null;
    this.subPage = null;
    this.started = null;
    this.ended = null;
    this.requestCount = 0;
    this.firstRequestTime = null;
    this.lastRequestTime = null;
    this.requestDetails = new Map();
    this.renderStartTime = null;
  }

  /**
   * Log the completed page view to analytics
   */
  private logPageView(): void {
    if (this.page == null || this.started == null || this.ended == null) {
      return;
    }

    const pageName = this.getPageName(this.module, this.page, this.subPage);
    const duration = this.ended.getTime() - this.started.getTime();
    const url = window.location.href.slice(window.location.origin.length);
    const pagePerformanceMetrics = this.calculateRequestTimes();

    const customProperties: PageViewProperties = {
      module: this.module,
      page: this.page,
      ...pagePerformanceMetrics,
    };

    if (this.subPage != null) {
      customProperties.subPage = this.subPage;
    }

    // Add URL query parameters to tracking properties
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.forEach((value: string, key: string) => {
      customProperties[`query_${key}`] = value;
    });
    this.loggingService.logPageView(pageName, url, customProperties, duration);

    this.lastLoggedPageName = pageName;
    this.clear();
  }

  /**
   * Calculate detailed timing metrics for the current page load
   */
  private calculateRequestTimes(): PagePerformanceMetrics {
    // If we don't have request timing data, return null
    if (!this.firstRequestTime && this.requestDetails.size === 0) {
      return null;
    }

    // Calculate network time (sum of all request durations)
    const networkTime = Array.from(this.requestDetails.values()).reduce((total, req) =>
      total + (req.duration || 0), 0);

    // Calculate render time if available
    let renderTime: number = null;
    // Calculate total page load time
    let totalPageLoadTime = null;

    const navigationEntries = performance.getEntriesByType('navigation');
    if (navigationEntries.length > 0) {
      const navTiming = navigationEntries[0] as PerformanceNavigationTiming;

      // Only calculate render time if we have a valid renderStartTime
      if (this.renderStartTime && this.renderStartTime > navTiming.fetchStart) {
        renderTime = Math.max(navTiming.loadEventEnd, this.renderStartTime ?? 0) - navTiming.fetchStart; ;
      }

      // if page loads content asynchronously after the complete page load then count Time to First Contentful Paint (FCP)
      totalPageLoadTime = navTiming.loadEventEnd - navTiming.fetchStart;
    }

    return {
      networkTime,
      renderTime,
      totalPageLoadTime,
    };
  }

  private clearTimer(): void {
    if (this.timerId != null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  /**
   * Generate a consistent page name from module, page, and subPage
   */
  private getPageName(module: ModuleType, page: PageType, subPage: SubPageType): string {
    return subPage != null ? `${module}/${page}/${subPage}` : `${module}/${page}`;
  }
}
